import { useCallback, useEffect, useRef } from "react";
import { useProductBranding } from "src/hooks/useProductBranding";

const MAX_NOTIFICATION_LENGTH = 100;
const TITLE_FLASH_INTERVAL_MS = 2000;

interface UseChatNotificationOptionsType {
  isWaitingForResponse: boolean;
  lastResponseText?: string;
  lastResponseId?: string;
}

interface UseChatNotificationReturnType {
  /** Call this inside the send handler (during a user gesture) to request permission */
  requestNotificationPermission: () => void;
}

export function requestChatNotificationPermission(): void {
  if (typeof window === "undefined") return;
  if ("Notification" in window && Notification.permission === "default") {
    Notification.requestPermission();
  }
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1).trimEnd() + "…";
}

/** Extract the last agent message's content and id from a conversation's messages array. */
export function getLastAgentResponse(
  messages: ReadonlyArray<
    { id: string; messageRole: string; messageType?: string | null; content: string | null } | null
  > | null | undefined,
): { text: string | undefined; id: string | undefined } {
  if (!messages || messages.length === 0) return { text: undefined, id: undefined };
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!message) continue;
    if (message.messageRole !== "AGENT") continue;
    if (message.messageType === "AGENT_PROCESS") continue;
    const content = message.content?.trim();
    if (!content) continue;
    return { text: content, id: message.id };
  }
  return { text: undefined, id: undefined };
}

export function useChatNotification({
  isWaitingForResponse,
  lastResponseText,
  lastResponseId,
}: UseChatNotificationOptionsType): UseChatNotificationReturnType {
  const { productName } = useProductBranding();
  const prevWaitingRef = useRef(false);
  const lastNotifiedIdRef = useRef<string | undefined>(undefined);
  const notificationRef = useRef<Notification | null>(null);
  const originalTitleRef = useRef<string | null>(null);
  const flashIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Must be called during a user gesture (click/keypress) for the browser to show the prompt
  const requestNotificationPermission = useCallback(() => {
    requestChatNotificationPermission();
  }, []);

  // Fire notification + title flash when response completes while tab is hidden
  useEffect(() => {
    const wasWaiting = prevWaitingRef.current;
    prevWaitingRef.current = isWaitingForResponse;

    // Only notify when waiting transitions false AND this response id wasn't already notified
    const hasNewResponse = lastResponseId !== undefined && lastResponseId !== lastNotifiedIdRef.current;
    const responseCompleted = !isWaitingForResponse && wasWaiting;
    if (responseCompleted && hasNewResponse) {
      if (document.hidden) {
        // Title flash — alternate between notification and original title
        if (originalTitleRef.current === null) {
          originalTitleRef.current = document.title;
        }
        const notificationTitle = `⦁ New Message from ${productName}`;
        document.title = notificationTitle;
        if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);
        let showNotification = true;
        flashIntervalRef.current = setInterval(() => {
          showNotification = !showNotification;
          document.title = showNotification ? notificationTitle : originalTitleRef.current ?? "";
        }, TITLE_FLASH_INTERVAL_MS);

        // OS notification
        if ("Notification" in window && Notification.permission === "granted") {
          const body = lastResponseText
            ? truncate(lastResponseText, MAX_NOTIFICATION_LENGTH)
            : "New message is ready.";
          const notification = new Notification(productName, {
            body,
            tag: "chat-response",
          });
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
          notificationRef.current = notification;
        }
      }

      // Mark this response handled on completion to avoid stale-id notifications later.
      lastNotifiedIdRef.current = lastResponseId;
    }
  }, [isWaitingForResponse, productName, lastResponseText, lastResponseId]);

  // Restore title and close notification when tab regains focus or on unmount
  useEffect(() => {
    const resetPageAttentionState = () => {
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
        flashIntervalRef.current = null;
      }
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current;
        originalTitleRef.current = null;
      }
      if (notificationRef.current) {
        notificationRef.current.close();
        notificationRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        resetPageAttentionState();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      resetPageAttentionState();
    };
  }, []);

  return { requestNotificationPermission };
}
